// Sidebar and header trigger for the command palette.
//
// variant="full"     — faux-input style: Search icon + "Search…" text +
//                      keyboard shortcut label (⌘K / Ctrl K). Used in the
//                      desktop expanded sidebar between the logo header and nav,
//                      and at the top of the mobile drawer nav.
//
// variant="iconOnly" — icon-only button. Used inside the collapsed desktop
//                      sidebar's icon rail (wrapped in a RailTooltip), and in
//                      the mobile sticky header. Idle colour matches the rail's
//                      other 44px controls (text-slate-600 light / slate-400 dark).
//
// Both variants call usePaletteContext().open() on click; neither accepts
// keyboard focus away from natural document order (no tabIndex manipulation).
//
// Touch-target sizing: iconOnly is w-11 h-11 (44 px) to meet the WCAG 2.5.5
// / Apple HIG minimum. The full variant enforces min-h-[44px] so the mobile
// drawer placement also meets the guideline.

import { Search } from "lucide-react";
import { usePaletteContext } from "./PaletteProvider";
import { shortcutLabel } from "./platform";

export interface PaletteTriggerProps {
  variant: "full" | "iconOnly";
  className?: string;
}

export function PaletteTrigger({ variant, className = "" }: PaletteTriggerProps) {
  const { open } = usePaletteContext();
  const label = shortcutLabel();

  if (variant === "full") {
    return (
      <button
        type="button"
        aria-label={`Search (${label})`}
        onClick={open}
        className={`group flex items-center w-full min-h-[44px] gap-2 px-3 py-2 rounded-lg text-body-sm
          bg-slate-100 dark:bg-slate-700/60
          hover:bg-slate-200 dark:hover:bg-slate-700
          text-slate-500 dark:text-slate-400
          border border-slate-200 dark:border-slate-600
          transition-colors ${className}`}
      >
        <Search className="h-4 w-4 shrink-0 text-slate-400 dark:text-slate-500" aria-hidden="true" />
        <span className="flex-1 text-left truncate">Search…</span>
        <kbd
          className="shrink-0 font-mono text-caption px-1.5 py-0.5 rounded bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-400 dark:text-slate-500"
          aria-hidden="true"
        >
          {label}
        </kbd>
      </button>
    );
  }

  // iconOnly
  return (
    <button
      type="button"
      aria-label={`Search (${label})`}
      onClick={open}
      className={`flex items-center justify-center w-11 h-11 rounded-lg
        text-slate-600 dark:text-slate-400
        hover:bg-slate-100 dark:hover:bg-slate-700
        transition-colors ${className}`}
    >
      <Search className="h-5 w-5" aria-hidden="true" />
    </button>
  );
}
